/** Reporting (spec §16): shapes and the bits of math worth testing on their own. */

export const AGING_BUCKETS = ["current", "1-30", "31-60", "61-90", "90+"] as const;
export type AgingBucket = (typeof AGING_BUCKETS)[number];

/** Days past due (due date, else 30 days after sending) → bucket. Dates are YYYY-MM-DD. */
export function agingBucket(due: string, today: string): AgingBucket {
  const days = Math.floor((Date.parse(`${today}T00:00:00Z`) - Date.parse(`${due}T00:00:00Z`)) / 86_400_000);
  if (days <= 0) return "current";
  if (days <= 30) return "1-30";
  if (days <= 60) return "31-60";
  if (days <= 90) return "61-90";
  return "90+";
}

/** Same range one year earlier (Feb 29 → Feb 28). */
export function priorYear(from: string, to: string): { from: string; to: string } {
  const back = (d: string) => {
    const [y, m, day] = d.split("-").map(Number) as [number, number, number];
    const last = new Date(Date.UTC(y - 1, m, 0)).getUTCDate();
    return `${y - 1}-${String(m).padStart(2, "0")}-${String(Math.min(day, last)).padStart(2, "0")}`;
  };
  return { from: back(from), to: back(to) };
}

/** Change from the prior period as a whole percent; null when there's nothing to compare to. */
export function percentChange(now: number, before: number): number | null {
  if (!before) return null;
  return Math.round(((now - before) / Math.abs(before)) * 100);
}

export interface OverviewNumbers {
  salesCents: number;
  salesCount: number;
  collectedCents: number;
  newJobs: number;
}

export interface ReportsOverview {
  from: string;
  to: string;
  /** Null without the business overview report. */
  now: OverviewNumbers | null;
  priorYear: OverviewNumbers | null;
  /** As of today, not the range. */
  arOutstandingCents: number | null;
  depreciationToCollectCents: number | null;
  /** Empty without the rep leaderboard report. */
  leaderboard: { rep: string; color: string; salesCents: number; salesCount: number; collectedCents: number }[];
}

export interface PipelineRow {
  workflow: string;
  stage: string;
  kind: "open" | "closed" | "lost";
  jobs: number;
  contractCents: number;
}

export interface AgingRow {
  invoiceId: string;
  number: number;
  jobId: string;
  jobLabel: string;
  due: string;
  balanceCents: number;
  bucket: AgingBucket;
}

export interface CloseRate {
  sent: number;
  signed: number;
  declined: number;
  open: number;
  signedCents: number;
  /** Signed ÷ (signed + declined), whole percent; null with no decisions yet. */
  ratePercent: number | null;
}

export interface LeadSourceRow {
  source: string;
  jobs: number;
  sold: number;
  contractCents: number;
}

export interface JobPnlRow {
  jobId: string;
  jobLabel: string;
  stage: string;
  contractCents: number;
  collectedCents: number;
  costCents: number;
  marginCents: number;
  marginPercent: number | null;
}

/** "How did we do?" answers for surveys sent in a range. */
export interface SatisfactionReport {
  sent: number;
  answered: number;
  /** Average of the answers, one decimal; null with none. */
  average: number | null;
  /** How many of each score, 1 to 5. */
  counts: [number, number, number, number, number];
  recent: { jobId: string; jobNumber: number; customer: string; score: number; comment: string; answeredAt: string }[];
}
